import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiClient } from '../api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'google_id_token';
const USER_KEY = 'google_user';

function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  if (token === 'disabled') return false;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  // Consider expired 60s early to avoid edge cases
  return Date.now() / 1000 > payload.exp - 60;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem(USER_KEY);
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientId, setClientId] = useState(null);

  const signOut = useCallback(() => {
    setUser(null);
    setToken(null);
    setError(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    window.google?.accounts?.id?.disableAutoSelect();
  }, []);

  const handleCredentialResponse = useCallback((response) => {
    const idToken = response.credential;
    const payload = parseJwt(idToken);

    if (!payload) {
      setError('Failed to parse token');
      return;
    }

    if (!payload.email?.endsWith('@sleeper.app')) {
      setError('Access restricted to @sleeper.app accounts');
      return;
    }

    const userInfo = {
      email: payload.email,
      name: payload.name,
      picture: payload.picture,
    };

    setToken(idToken);
    setUser(userInfo);
    setError(null);
    localStorage.setItem(TOKEN_KEY, idToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
  }, []);

  // Fetch Google Client ID from backend, then initialize GSI
  useEffect(() => {
    let cancelled = false;

    async function init() {
      // 1. Fetch client ID from backend
      let googleClientId;
      let authDisabled = false;
      try {
        const { data } = await apiClient.get('/auth/config');
        googleClientId = data.google_client_id;
        authDisabled = data.auth_disabled;
      } catch (e) {
        if (!cancelled) setError('Failed to load auth config');
        if (!cancelled) setLoading(false);
        return;
      }

      if (cancelled) return;

      // Auth disabled on backend — skip Google sign-in entirely
      if (authDisabled) {
        const devUser = { email: 'dev@local', name: 'Local Dev', picture: '' };
        setUser(devUser);
        setToken('disabled');
        localStorage.setItem(TOKEN_KEY, 'disabled');
        localStorage.setItem(USER_KEY, JSON.stringify(devUser));
        setLoading(false);
        return;
      }

      setClientId(googleClientId);

      // 2. Load GSI script
      await new Promise((resolve) => {
        if (window.google?.accounts?.id) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        document.head.appendChild(script);
      });

      if (cancelled) return;

      // 3. Wait for GSI to be ready
      const waitForGsi = () => new Promise((resolve) => {
        const check = () => {
          if (window.google?.accounts?.id) resolve();
          else setTimeout(check, 50);
        };
        check();
      });
      await waitForGsi();

      if (cancelled) return;

      // 4. Initialize
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
        auto_select: true,
      });

      // Check if existing token is still valid
      const savedToken = localStorage.getItem(TOKEN_KEY);
      if (savedToken && !isTokenExpired(savedToken)) {
        setLoading(false);
        return;
      }

      // Token expired or missing - clear state
      if (savedToken) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setToken(null);
      }

      setLoading(false);
    }

    init();
    return () => { cancelled = true; };
  }, [handleCredentialResponse]);

  // Check token expiry periodically
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      if (isTokenExpired(token)) {
        signOut();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [token, signOut]);

  const value = {
    user,
    token,
    loading,
    error,
    signOut,
    isAuthenticated: !!user && !!token && (token === 'disabled' || !isTokenExpired(token)),
    clientId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
